import React from 'react';
import { Cog6ToothIcon, MoonIcon } from '@heroicons/react/24/outline';
import { useInView, usePlayback, usePrefersReducedMotion } from '../pbi-development/coworkChatHooks';
import SemanticHRPbiPanel, { resolvePbiBookmarkState, resolvePbiDashboardView } from './SemanticHRPbiPanel';
import {
  SEMHR_PBI_GALLERY_DELAYS,
  SEMHR_PBI_GALLERY_LOOP_PAUSE,
  SEMHR_PBI_GALLERY_STEPS,
} from './semanticHrTourData';
import { PRODUCT_MEDIA } from '../../../../content/productMedia';

function getCurrentStep(visibleCount, steps) {
  if (visibleCount <= 0) return null;
  return steps[Math.min(visibleCount - 1, steps.length - 1)];
}

function GalleryTopBar() {
  return (
    <header className="grid shrink-0 grid-cols-3 items-center border-b border-ink-200/80 bg-white px-2 py-1.5">
      <div aria-hidden="true" />
      <div className="flex justify-center">
        <div className="flex items-center gap-0.5 rounded-full border border-ink-200/90 bg-surface-subtle/60 p-0.5 text-[8px]">
          <span className="rounded-full bg-brand-700 px-2 py-0.5 font-semibold text-white">Copilot</span>
          <span className="rounded-full px-2 py-0.5 font-semibold text-ink-500">Builder</span>
        </div>
      </div>
      <div className="flex items-center justify-end gap-0.5 text-ink-400">
        <Cog6ToothIcon className="h-2.5 w-2.5" aria-hidden="true" />
        <MoonIcon className="h-2.5 w-2.5" aria-hidden="true" />
      </div>
    </header>
  );
}

export default function SemanticHRPbiGalleryVisual({ className = '' }) {
  const reducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView(0.12);
  const { visibleCount } = usePlayback(
    inView,
    {
      steps: SEMHR_PBI_GALLERY_STEPS,
      stepDelays: SEMHR_PBI_GALLERY_DELAYS,
      loopPause: SEMHR_PBI_GALLERY_LOOP_PAUSE,
    },
    reducedMotion,
  );

  const currentStep = getCurrentStep(visibleCount, SEMHR_PBI_GALLERY_STEPS);
  const dashView = resolvePbiDashboardView(currentStep) ?? 'termination';
  const bookmarkState = resolvePbiBookmarkState(currentStep);
  const caption = PRODUCT_MEDIA.semantichr_split_screen1?.caption;

  return (
    <figure
      className={`ia-semhr-pbi-gallery overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm ${className}`.trim()}
    >
      <div
        ref={ref}
        className="ia-semhr-pbi-gallery-frame pointer-events-none select-none bg-surface-subtle/30 leading-none"
        role="img"
        aria-label="Mock-up of the Power BI bookmark gallery inside Semantic HR, with the Copilot and Builder mode switch"
      >
        <div className="ia-semhr-pbi-gallery-shell" aria-hidden="true">
          <GalleryTopBar />
          <div className="ia-semhr-pbi-gallery-stage">
            <div className="ia-semhr-pbi-gallery-crop" aria-hidden="true">
              <div className="ia-semhr-pbi-gallery-split">
                <div className="ia-semhr-pbi-gallery-chat-stub" />
                <SemanticHRPbiPanel
                  view={dashView}
                  bookmarkState={bookmarkState}
                  galleryMode
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {caption ? (
        <figcaption className="border-t border-ink-100 bg-surface-subtle/60 px-4 py-3 text-xs leading-relaxed text-ink-500">
          <span className="mr-2 inline-flex items-center gap-1.5" aria-hidden="true">
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
              Semantic HR
            </span>
          </span>
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
